import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionIcon, Box, Combobox, Divider, factory, Text, TextInput, Title, useCombobox, useProps, useUncontrolled } from "@coveord/plasma-mantine";
import { Formatting, NumberFormatter } from "@core/format";
import { CrossSize16Px, FilterSize16Px } from "@coveord/plasma-react-icons";
import { clsx } from "clsx";
import { DefaultFacetItem } from "./DefaultFacetItem.js";
import classes from "./Facet.module.css";
import { FacetScrollArea } from "./FacetScrollArea.js";
import { groupOptions } from "../../utilities/group-options.js";
var defaultProps = {
    searchPlaceholder: "Search",
    nothingFound: "No matching items",
    placeholder: "No items",
    height: 200,
    limit: Infinity,
    itemComponent: DefaultFacetItem,
    listComponent: FacetScrollArea,
    itemCountFormatter: Formatting.toFunction(NumberFormatter.integerFull)
};
export var Facet = factory(function(_props, ref) {
    var props = useProps("Facet", defaultProps, _props);
    var className = props.className, data = props.data, onChange = props.onChange, _initialSelection = props.initialSelection, initialSelection = _initialSelection === void 0 ? [] : _initialSelection, selection = props.selection, ItemComponent = props.itemComponent, ListComponent = props.listComponent, itemCountFormatter = props.itemCountFormatter, searchPlaceholder = props.searchPlaceholder, query = props.query, _hideSearch = props.hideSearch, hideSearch = _hideSearch === void 0 ? data.length <= 7 : _hideSearch, onSearch = props.onSearch, _filter = props.filter, filter = _filter === void 0 ? defaultFilter : _filter, nothingFound = props.nothingFound, placeholder = props.placeholder, title = props.title, height = props.height, radius = props.radius, __staticSelector = props.__staticSelector, classNames = props.classNames, styles = props.styles, limit = props.limit, unstyled = props.unstyled, others = _object_without_properties(props, [
        "className",
        "data",
        "onChange",
        "initialSelection",
        "selection",
        "itemComponent",
        "listComponent",
        "itemCountFormatter",
        "searchPlaceholder",
        "query",
        "hideSearch",
        "onSearch",
        "filter",
        "nothingFound",
        "placeholder",
        "title",
        "height",
        "radius",
        "__staticSelector",
        "classNames",
        "styles",
        "limit",
        "unstyled"
    ]);
    var combobox = useCombobox();
    var ref1 = _sliced_to_array(useUncontrolled({
        value: query,
        defaultValue: "",
        finalValue: "",
        onChange: onSearch
    }), 2), search = ref1[0], handleSearch = ref1[1];
    var ref2 = _sliced_to_array(useUncontrolled({
        value: selection,
        defaultValue: initialSelection,
        finalValue: [],
        onChange: onChange
    }), 2), _selection = ref2[0], handleSelection = ref2[1];
    var unGroupedItems = [];
    var groupedItems = [];
    var filteredData = data.filter(function(item) {
        return filter(search, item);
    }).slice(0, limit);
    var sortedData = groupOptions({
        data: filteredData
    });
    var handleValueSelect = function(val) {
        return handleSelection(_selection.includes(val) ? _selection.filter(function(v) {
            return v !== val;
        }) : _to_consumable_array(_selection).concat([
            val
        ]));
    };
    var groupName = null;
    sortedData.forEach(function(item) {
        var itemComponent = /*#__PURE__*/ _jsx(Combobox.Option, {
            active: _selection.includes(item.value),
            "aria-selected": _selection.includes(item.value),
            value: item.value,
            onMouseEnter: function() {
                return combobox.resetSelectedOption();
            },
            className: clsx(classes.facetItem),
            tabIndex: data.length > 8 ? -1 : 1,
            children: /*#__PURE__*/ _jsx(ItemComponent, {
                data: item,
                selected: _selection.includes(item.value),
                countFormatter: itemCountFormatter
            })
        }, item.value);
        if (!item.group) {
            unGroupedItems.push(itemComponent);
        } else {
            if (groupName !== item.group) {
                groupName = item.group;
                groupedItems.push(/*#__PURE__*/ _jsx("div", {
                    className: classes.separator,
                    children: /*#__PURE__*/ _jsx(Divider, {
                        classNames: {
                            label: classes.separatorLabel
                        },
                        label: groupName
                    })
                }, groupName));
            }
            groupedItems.push(itemComponent);
        }
    });
    if (groupedItems.length > 0 && unGroupedItems.length > 0) {
        unGroupedItems.unshift(/*#__PURE__*/ _jsx("div", {
            className: classes.separator,
            children: /*#__PURE__*/ _jsx(Divider, {
                unstyled: unstyled,
                classNames: {
                    label: classes.separatorLabel
                }
            })
        }));
    }
    return /*#__PURE__*/ _jsx(Box, _object_spread_props(_object_spread({
        className: clsx(classes.facet, className)
    }, others), {
        children: /*#__PURE__*/ _jsxs(Combobox, {
            store: combobox,
            onOptionSubmit: handleValueSelect,
            children: [
                /*#__PURE__*/ _jsx(Combobox.EventsTarget, {
                    children: /*#__PURE__*/ _jsxs(Box, {
                        className: classes.facetHeader,
                        children: [
                            title ? /*#__PURE__*/ _jsx(Title, {
                                order: 5,
                                children: title
                            }) : null,
                            /*#__PURE__*/ _jsx(TextInput, {
                                unstyled: unstyled,
                                value: search,
                                onChange: function(event) {
                                    handleSearch(event.currentTarget.value);
                                    combobox.updateSelectedOptionIndex();
                                },
                                placeholder: searchPlaceholder,
                                "aria-hidden": hideSearch,
                                className: clsx(classes.facetSearch, _define_property({}, classes.hiddenSearch, hideSearch)),
                                rightSection: search ? /*#__PURE__*/ _jsx(ActionIcon.Quaternary, {
                                    "aria-label": "clear search",
                                    color: "gray",
                                    onClick: function() {
                                        handleSearch("");
                                    },
                                    children: /*#__PURE__*/ _jsx(CrossSize16Px, {
                                        height: 16
                                    })
                                }) : /*#__PURE__*/ _jsx(FilterSize16Px, {
                                    height: 16
                                })
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: classes.facetBody,
                    children: /*#__PURE__*/ _jsx(ListComponent, {
                        className: classes.facetItems,
                        mah: height,
                        style: {
                            overflow: "auto",
                            position: "relative"
                        },
                        children: /*#__PURE__*/ _jsx(Combobox.Options, {
                            children: groupedItems.length > 0 || unGroupedItems.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    groupedItems,
                                    unGroupedItems
                                ]
                            }) : /*#__PURE__*/ _jsx(Combobox.Empty, {
                                children: /*#__PURE__*/ _jsx(Text, {
                                    color: "dimmed",
                                    unstyled: unstyled,
                                    size: "sm",
                                    ta: "center",
                                    my: "sm",
                                    children: !query && placeholder ? placeholder : nothingFound
                                })
                            })
                        })
                    })
                })
            ]
        })
    }));
});
Facet.displayName = "Facet";
var defaultFilter = function(query, item) {
    return item.label.toLowerCase().trim().includes(query.toLowerCase().trim()) || item.value.toLowerCase().trim().includes(query.toLowerCase().trim());
};

//# sourceMappingURL=Facet.js.map